
import { Component, Mixins } from "vue-property-decorator";
import { Getter } from "vuex-class";
import GenericTable, { TableHeader } from '../table/generic-table.vue';
import GenericUpdateModal, { ModalItem } from '../table/generic-update-modal.vue';
import { PaginatedSearchQueryParams, UserPermissions } from '@/store/types'
import organizationApi from "@/services/api.organizationSettings";
import PaginatedSearch, { PaginatedSearchRequest } from '@/mixins/PaginatedSearch';
import { namespace } from "vuex-class";
import AreaTreeSelect from "../form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import { MediaMatrixLayout, MediaMatrixSaveRequest, MediaMatrixLayoutOption } from "@/types/organization-settings/MediaMatrixLayout";
const SMCamerasState = namespace("siteMonitorCameras");

@Component({
	components: {
		"generic-table": GenericTable,
		"generic-update-modal": GenericUpdateModal,
	}
})
export default class MediaMatrixLayoutSetup extends Mixins(PaginatedSearch){
	@Getter("getPermissions") permissions: UserPermissions;
	@SMCamerasState.Getter("getLayouts") layouts: number[][];


	private isLoading: boolean = false;
	private totalRecords: number = 0;
	private paginatedSearchQueryParams: PaginatedSearchQueryParams = {
		searchTerm: null,
		page: 1,
		pageSize: 25
	};

	private records: MediaMatrixLayout[] = [];
	
    private get options(): MediaMatrixLayoutOption[] {
        return this.layouts.map((layout, index) => {
            return {
                title: this.convertLayoutToFriendlyText(layout),
                layoutIndex: index,
            };
        });
    }

    private convertLayoutToFriendlyText(layout: number[]): string {
        return `${layout}`.replace(",", "x")
    }
    
	public columns: TableHeader[] = [
		{
			title: "Area",
			key: "groupTitle",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The Area Title",
			searchable: true,
			visible: true,
			dataType: "input",
			isTermLabel: true,
			sortable: true,
			isSortedByDefault: true,
		},
		{
			title: "Layout",
			key: "layout",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "Any child areas will inherit the layout of their parent area, unless they have their own setting.",
			searchable: true,
			visible: true,
			dataType: "input",
			isTermLabel: false,
			sortable: false
		}
    ];  

	private async save(data: MediaMatrixSaveRequest, isUpdate = false): Promise<void> {
		try {
			const { groupId, layoutIndex } = data;
			if (!isUpdate) {
				if (this.records.some(record => record.groupId === groupId)) {
					this.notify('A layout for this area already exists.', 'error');
					return;
				}
			} 

			await organizationApi.CreateOrUpdateMediaMatrixLayoutSettings(groupId, layoutIndex);
			await this.retreiveLayouts();
		} catch (error) {
			console.error("Error saving Media Matrix Layout settings:", error);
		}
	}

	private notify(message: string, type: string = "info"): void { 
		this.$notify({
			type,
			text: message,
			duration: 3000
		});
	}

    private async mounted(): Promise<void> {
		await this.retreiveLayouts();
	}

	private async retreiveLayouts(): Promise<void> { 
		this.isLoading = true;
		try {
			const pageRequest: PaginatedSearchRequest = this.generateNewPaginatedSearchRequest(this.paginatedSearchQueryParams);

			const { data, totalRecords } = await organizationApi.GetMediaMatrixLayoutSettings(pageRequest);
			this.totalRecords = totalRecords;
			this.records = data.map((item: MediaMatrixLayout) => ({
				...item,
				layout: this.convertLayoutToFriendlyText(this.layouts[item.layoutIndex]),
				originalGroupId: item.groupId // Store original groupId for updates
			}));
		} catch (error) {
			console.error("Error retrieving Media Matrix Layouts:", error);
		}
		this.isLoading = false;
	}

	private async deleteRecord(record: MediaMatrixLayout): Promise<void> { 
		try {
			await organizationApi.DeleteMediaMatrixLayoutSettings(record.groupId);
			await this.retreiveLayouts();
		} catch (error) {
			console.error("Error deleting Media Matrix Layout:", error);
		}
	}

	private async updatePaginationParameters(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		this.paginatedSearchQueryParams = paginatedSearchQueryParams;
		await this.retreiveLayouts();
	}

    public get modalItems(): ModalItem[] {
		return [
			{
				title: "Area",
				key: "groupId",
				dataType: "component",
				description: "Any child areas will inherit the layout of their parent area, unless they have their own setting.",
				required: true,
				data: AreaTreeSelect,
				props: {
					reduce: (area: AreaNode) => area.id,
					clearable: false,
					appendToBody: true,
				},
				readOnlyIfEdit: true,
            },
            {
                title: "Layout",
                key: "layoutIndex",
                dataType: "vselect3",
				data: this.options,
				props: {
					reduce: (option: any) => option.layoutIndex,
				}
			},
			
            {
                title: "Original Group ID",
                key: "originalGroupId",
                dataType: "number",
				data: this.options,
				props: {
					reduce: (option: any) => option.originalGroupId,
				},
				visible: false
			},
		]
	}
}
